import chatsApi from "api/chats";
import {IChatMessage} from "api/types";
import Modal from "components/Modal";
import ModalLayout from "components/ModalLayout";
import useAuthContext from "contexts/Auth/useAuthContext";
import {handleError} from "helpers/errors";
import {AwaitableModal} from "hooks/useAwaitableModal";
import {useState} from "react";
import {useLocation} from "react-router";

const SearchMessagesModal: AwaitableModal<{selected: IChatMessage}> = ({open, onCancel, resolve}) => {
  const chat = useLocation().pathname.split("/").pop() as string;
  const me = useAuthContext()?.uid;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IChatMessage[]>();
  const [loading, setLoading] = useState(false);

  const onSearch = () => {
    if (loading) return;
    if (query.length < 3) return handleError("Search query must be at least 3 characters long");
    setLoading(true);
    setResults(undefined);
    chatsApi
      .searchMessages(chat, query)
      .then(setResults)
      .finally(() => setLoading(false));
  };

  return (
    <Modal open={open} close={onCancel}>
      <ModalLayout
        title="Search Messages"
        scrollable
        footer={
          <button onClick={onCancel} className="btn-secondary ml-auto">
            Close
          </button>
        }
      >
        {/* Search input */}
        <div className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
            placeholder="Search messages…"
            className="grow"
          />
          <button onClick={onSearch} className="btn-primary shrink-0">
            Search
          </button>
        </div>

        {/* Results */}
        <div className="flex flex-col gap-2">
          {!loading && !results && (
            <span className="text-xs text-brand-muted text-center py-4">Type above and press Search</span>
          )}
          {loading && <span className="text-xs text-brand-muted text-center py-4">Searching…</span>}
          {results && !results.length && (
            <span className="text-xs text-brand-muted text-center py-4">No results found</span>
          )}
          {results
            ?.slice(0)
            .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
            .map((message) => (
              <div
                key={message.id}
                onClick={() => resolve({selected: message})}
                className="flex items-center gap-3 justify-end cursor-pointer hover:translate-x-0.5 transition-transform group"
              >
                <span className="text-xs text-brand-muted">{new Date(message.createdAt).toLocaleString()}</span>
                <span
                  className="text-sm px-3 py-2 rounded-xl"
                  style={{
                    background: message.author === me ? "#282D4D" : "#F5F3F0",
                    color: message.author === me ? "#fff" : "#282D4D",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {message.content}
                </span>
              </div>
            ))}
        </div>
      </ModalLayout>
    </Modal>
  );
};

export default SearchMessagesModal;
